import React, { useState } from 'react';
import Star from '@assets/icons/star';
import MathBanner from '@assets/icons/math-banner';
import EnglishBanner from '@assets/icons/english-banner';
import ChineseBanner from '@assets/icons/chinese-banner';
import ScienceBanner from '@assets/icons/science-banner';
import { CDN_URL } from '@utils/constants/url.constants';
import { subjectDisplayNameFromSlug } from '@utils/helpers/SubjectParser';
import CircleInfo from '@assets/icons/circle-info';
import { Button } from 'geniebook-ui/dist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIsJCUser } from '@hooks/user.hook';
import { subjectIdFromName } from '@utils/helpers/subjectIdFromName';
import { useViewWidth } from '@utils/helpers/viewWidth';

export interface BannerSkillLevelProps {
  skillLevel: number;
  subject: string;
  className?: string;
  onClickLearnMore?: () => void;
  onClickHowCalculateSkillLevel?: () => void;
}

const svgClass = 'fill-current text-white w-[320px] opacity-20';
const breakpoint = require('geniebook-ui/dist/themes/breakpoint');
const BannerSkillLevel = (props: BannerSkillLevelProps) => {
  function bgColorBanner(subject: string): string {
    switch (subject) {
      case 'mathematics':
      case 'e-math':
      case 'a-math':
      case 'vietnam-mathematics':
      case 'foundation-mathematics':
      case 'h2-math':
        return '#FFAA4D';
      case 'english':
      case 'young-learners-english':
        return '#26A7DF';
      case 'chinese':
      case 'chinese-c2':
        return '#B33277';
      case 'h2-physics':
      case 'h2-chemistry':
      case 'h2-biology':
        return '#615e9b';
      default:
        return '#2E2C74';
    }
  }
  function iconBanner(subject: string): React.ReactNode {
    switch (subject) {
      case 'mathematics':
      case 'e-math':
      case 'a-math':
      case 'vietnam-mathematics':
      case 'foundation-mathematics':
        return <MathBanner className={svgClass} data-testid={'math-banner'} />;
      case 'english':
      case 'young-learners-english':
        return (
          <EnglishBanner className={svgClass} data-testid={'english-banner'} />
        );
      case 'chinese':
      case 'chinese-c2':
        return (
          <ChineseBanner className={svgClass} data-testid={'chinese-banner'} />
        );
      default:
        return (
          <ScienceBanner className={svgClass} data-testid={'science-banner'} />
        );
    }
  }

  const { isJC } = useIsJCUser({
    subjectId: subjectIdFromName(props.subject),
  });
  const [tooltip, setTooltip] = useState(false);
  const [showLearnMoreDropdown, setShowLearnMoreDropdown] =
    useState<boolean>(false);
  const width = useViewWidth();

  return (
    <div
      className={`${
        width < breakpoint.lg
          ? 'hidden'
          : 'flex items-center place-content-between rounded-2xl relative overflow-visible w-full'
      } ${props.className ?? ''}
      ${props.onClickLearnMore ? 'py-6' : 'py-8'} ${
        isJC === false ? 'pl-[158px]' : 'pl-8'
      } 
    `}
      style={{ backgroundColor: bgColorBanner(props.subject) }}
      data-testid={'container-banner'}
    >
      {isJC === false && (
        <img
          className={'absolute bottom-0 left-4 w-[126px]'}
          src={`${CDN_URL}assets/images/progress/banner-person.svg`}
          alt={''}
        />
      )}
      <div>
        <span
          className={
            'text-neutral-whiteboard text-xl font-bold align-middle leading-6'
          }
          data-testid={'subject-banner'}
        >
          Your{' '}
          <span className={'underline decoration-dashed'}>skill level</span>
          <span
            className={'relative'}
            onMouseOver={() => setTooltip(true)}
            onMouseLeave={() => setTooltip(false)}
            data-testid={'tooltip-button'}
          >
            <CircleInfo
              className={
                'fill-current align-baseline ml-1 text-neutral-whiteboard w-4 inline-block'
              }
            />
            {tooltip ? (
              <div
                className={'absolute z-30 left-[17px] top-[10px]'}
                data-testid={'tooltip'}
              >
                <div
                  className={
                    'absolute w-3 h-3 rotate-45 bg-neutral-graphite top-6 -translate-y-1/2 right-0'
                  }
                />
                <div
                  className={
                    'absolute right-2.5 top-6 text-neutral-whiteboard bg-neutral-graphite p-2 rounded-lg w-[372px] text-center translate-x-1/2 text-sm'
                  }
                >
                  Takes an average of your skill levels across topics in your
                  current year and one level lower.
                </div>
              </div>
            ) : null}
          </span>{' '}
          for {subjectDisplayNameFromSlug(props.subject)} is
        </span>
        <span
          className={
            'text-neutral-whiteboard text-[32px] font-black leading-6 align-middle'
          }
          data-testid={'skill-level-value'}
        >
          {' ' + (props.skillLevel == 0 ? '-' : props.skillLevel.toFixed(1))}
        </span>
        <span className={'inline-block w-4 align-middle pl-2'}>
          <Star
            className={
              'fill-current text-alert-warning w-4 stroke-neutral-chalk stroke-[20]'
            }
          />
        </span>
      </div>
      {isJC === false && (
        <div className={'absolute right-0 top-0 overflow-hidden h-full'}>
          {iconBanner(props.subject)}
        </div>
      )}
      {props.onClickLearnMore && (
        <div className="pr-8">
          <Button
            variant={'primary-navyblue'}
            className={'inline-flex ml-auto px-4 py-2 !font-black !h-auto'}
            size="md"
            onClick={() => {
              setShowLearnMoreDropdown(!showLearnMoreDropdown);
            }}
          >
            Learn More
          </Button>
        </div>
      )}
      {showLearnMoreDropdown && (
        <>
          <div
            className={
              'absolute bg-primary-navyblue text-white p-[8px] w-[240px] h-fit-content border-white border rounded-[8px] z-10 right-[33px] top-[72px]'
            }
          >
            <div
              className={
                'text-left py-2 px-2 hover:bg-primary-navyblue-bright hover:rounded-[8px]'
              }
              onClick={() => {
                setShowLearnMoreDropdown(false);
                props.onClickLearnMore && props.onClickLearnMore();
              }}
            >
              How to read skill levels?
            </div>
            <div
              className={
                'text-left py-2 px-2 hover:bg-primary-navyblue-bright hover:rounded-[8px]'
              }
              onClick={() => {
                setShowLearnMoreDropdown(false);
                props.onClickHowCalculateSkillLevel &&
                  props.onClickHowCalculateSkillLevel();
              }}
            >
              How are my skill levels calculated?
            </div>
            <div
              className={
                'text-left py-2 px-2 hover:bg-primary-navyblue-bright hover:rounded-[8px]'
              }
              onClick={() => {
                setShowLearnMoreDropdown(false);
                window.open(
                  'https://geniebook.atlassian.net/servicedesk/customer/portal/1/topic/28f20a86-57a7-4f0a-898a-30983bf2342a/article/1349058564',
                  '_blank',
                  'noreferrer',
                );
              }}
            >
              <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} />
              &nbsp;&nbsp;Browse complete FAQ
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BannerSkillLevel;
